import { useRef, type ReactNode, type CSSProperties } from 'react';

interface GlassCardProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  as?: 'div' | 'article' | 'section';
  onClick?: () => void;
}

export function GlassCard({ children, className = '', style, as: Tag = 'div', onClick }: GlassCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const spotlightRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    const spotlight = spotlightRef.current;
    if (!card || !spotlight) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -8;
    const rotateY = ((x - centerX) / centerX) * 8;

    card.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
    spotlight.style.background = `radial-gradient(circle at ${x}px ${y}px, var(--color-accent-subtle) 0%, transparent 60%)`;
    spotlight.style.opacity = '1';
  };

  const handleMouseLeave = () => {
    const card = cardRef.current;
    const spotlight = spotlightRef.current;
    if (!card || !spotlight) return;

    card.style.transform = 'perspective(800px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
    spotlight.style.opacity = '0';
  };

  return (
    <Tag
      ref={cardRef}
      className={`glass-card ${className}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: 'var(--color-surface-glass)',
        backdropFilter: `blur(var(--glass-blur)) saturate(var(--glass-saturate))`,
        WebkitBackdropFilter: `blur(var(--glass-blur)) saturate(var(--glass-saturate))`,
        border: '1px solid var(--color-surface-glass-border)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-glass)',
        transition: 'transform 0.15s ease-out, box-shadow 0.15s ease-out',
        willChange: 'transform',
        cursor: onClick ? 'pointer' : undefined,
        ...style,
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
      onKeyDown={onClick ? (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <div
        ref={spotlightRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0,
          transition: 'opacity 0.3s ease',
          pointerEvents: 'none',
          borderRadius: 'inherit',
        }}
      />
      {children}
    </Tag>
  );
}
